// Browser-based detection using TensorFlow.js
import * as cocoSsd from '@tensorflow-models/coco-ssd';
import '@tensorflow/tfjs';
import { IDetectionService, Detection, DetectionResult, DetectionOptions } from './detection.service';

type ModelBase = 'lite_mobilenet_v2' | 'mobilenet_v2';

export class BrowserDetectionService implements IDetectionService {
  private model: cocoSsd.ObjectDetection | null = null;
  private loading: boolean = false;
  private ready: boolean = false;
  private currentModelBase: ModelBase | null = null;

  async initialize(modelType: 'lite' | 'mobilenet' = 'lite'): Promise<void> {
    const modelBase: ModelBase = modelType === 'lite' ? 'lite_mobilenet_v2' : 'mobilenet_v2';
    
    // If already loaded with the same model, skip
    if (this.ready && this.currentModelBase === modelBase) {
      // Already initialized with this model
      return;
    }

    // If switching models, dispose old one first
    if (this.model && this.currentModelBase !== modelBase) {
      // Switching models
      this.dispose();
    }

    if (this.loading) {
      // Already loading
      return;
    }

    try {
      this.loading = true;
      // Loading model (keep this line for user feedback)
      const startTime = Date.now();
      
      this.model = await cocoSsd.load({
        base: modelBase
      });
      
      const loadTime = Date.now() - startTime;
      console.log(`Browser detection: ${modelBase} model loaded in ${loadTime}ms`);
      this.ready = true;
      this.currentModelBase = modelBase;
    } catch (error) {
      console.error('Browser detection: Failed to load model:', error);
      throw error;
    } finally {
      this.loading = false;
    }
  }

  isReady(): boolean {
    return this.ready && this.model !== null;
  }

  async detect(
    imageData: string | HTMLVideoElement | HTMLImageElement,
    options?: DetectionOptions
  ): Promise<DetectionResult> {
    if (!this.isReady()) {
      throw new Error('Model not initialized. Call initialize() first.');
    }

    const startTime = Date.now();
    
    // Default to more sensitive detection
    const maxNumBoxes = options?.maxNumBoxes ?? 50;  // Increased from default 20
    const minScore = options?.minScore ?? 0.3;       // Lowered from default 0.5 (50% → 30%)
    
    console.log(`🔍 Detection settings: maxDetections=${maxNumBoxes}, minConfidence=${(minScore * 100).toFixed(0)}%`);
    
    try {
      let imageElement: HTMLImageElement | HTMLVideoElement;

      if (typeof imageData === 'string') {
        // Convert base64 to image element
        imageElement = await this.base64ToImage(imageData);
      } else {
        imageElement = imageData;
      }

      const predictions = await this.model!.detect(imageElement, maxNumBoxes, minScore);
      const processingTime = Date.now() - startTime;

      const detections: Detection[] = predictions.map((pred: any) => ({
        class: pred.class,
        confidence: pred.score,
        boundingBox: {
          x: pred.bbox[0],
          y: pred.bbox[1],
          width: pred.bbox[2],
          height: pred.bbox[3]
        }
      }));

      // Detection complete (logged at higher level if needed)

      return {
        detections,
        processingTime,
        method: 'browser'
      };
    } catch (error) {
      console.error('Browser detection: Error during detection:', error);
      throw error;
    }
  }

  private base64ToImage(base64: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = base64;
    });
  }

  dispose(): void {
    if (this.model) {
      this.model.dispose();
      this.model = null;
      this.ready = false;
      // Model disposed
    }
  }
}

